// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
/**
 * DashboardPage - /dashboard
 * Vereenvoudigd voor 50+/65+ doelgroep.
 * Zone A: begroeting + voortgang + primaire actie
 * Zone B: modules (ingeklapt, uitklapbaar)
 * Zone C: dossiers
 * Reset verplaatst naar /settings.
 */
import { useEffect, useState } from 'react'
import { useNavigate }         from 'react-router-dom'
import {
  FolderOpen, Users, MessageSquare, Stethoscope,
  Flower2, Mail, ShieldCheck, ArrowRight, CheckCircle2,
  AlertCircle, Smartphone, Scale, CreditCard, Car, Briefcase, Compass,
} from 'lucide-react'
import { SkeletonList } from '@/components/feedback/LoadingState'
import { PersonalDataCard } from '@/components/dashboard/PersonalDataCard'
import { useAuthStore, useWizardStore } from '@/store'
import { getAllModuleProgress } from '@/services/profile'
import { loadTrustedPersons }    from '@/services/trustedPerson'
import { loadChildren }          from '@/services/wizard'
import { STEP_LABELS }  from '@/modules/wizard/WizardLayout'
import { ROUTES, WIZARD_ROUTE } from '@/lib/constants/routes'
import { cn, formatDateNL } from '@/lib/utils'
import type { ModuleProgress } from '@/types'
import { StatusPill, moduleStatusKind } from '@/components/ui/StatusPill'

// ── Module-definities ──────────────────────────────────────────────────────────

interface ModuleSummary {
  id: string; label: string; description: string
  route: string; icon: React.ReactNode; progress: number
  status: 'NOT_STARTED'|'IN_PROGRESS'|'COMPLETE'
  lastVisited?: string
}

const MODULE_DEFS = [
  { id: 'trusted_person', label: 'Vertrouwenspersoon',    description: 'Toegang na overlijden',       route: ROUTES.TRUSTED_PERSON,  icon: <Users         className="h-4 w-4" />, accent: 'sage' },
  { id: 'medical',        label: 'Medische wensen',       description: 'Behandelgrenzen',             route: ROUTES.WISHES_MEDICAL,  icon: <Stethoscope   className="h-4 w-4" />, accent: 'blue' },
  { id: 'funeral',        label: 'Uitvaartwensen',        description: 'Uitvaartinstructies',         route: ROUTES.WISHES_FUNERAL,  icon: <Flower2       className="h-4 w-4" />, accent: 'purple' },
  { id: 'farewell',       label: 'Persoonlijk afscheid',  description: 'Brieven voor naasten',        route: ROUTES.WISHES_FAREWELL, icon: <Mail          className="h-4 w-4" />, accent: 'rose' },
  { id: 'messages',       label: 'Berichtenservice',      description: 'Berichten na overlijden',     route: ROUTES.MESSAGES,        icon: <MessageSquare className="h-4 w-4" />, accent: 'sky' },
  { id: 'documents',      label: 'Documentwijzer',        description: 'Locatie van documenten',      route: ROUTES.DOCUMENTS,       icon: <FolderOpen    className="h-4 w-4" />, accent: 'orange' },
  { id: 'digital_estate', label: 'Digitale nalatenschap', description: 'Online accounts',             route: ROUTES.AI_GUIDE,        icon: <Smartphone    className="h-4 w-4" />, accent: 'indigo' },
  { id: 'notarial',       label: 'Notariële wensen',      description: 'Executeur, erfgenamen',       route: ROUTES.NOTARIAL,        icon: <Scale         className="h-4 w-4" />, accent: 'amber' },
  { id: 'financial',      label: 'Financieel overzicht',  description: 'Bank, pensioen, verzekering', route: ROUTES.FINANCIAL,       icon: <CreditCard    className="h-4 w-4" />, accent: 'emerald' },
  { id: 'vehicles',       label: 'Voertuigen & mobiliteit', description: 'Auto, motor, camper',        route: ROUTES.VEHICLES,        icon: <Car           className="h-4 w-4" />, accent: 'slate' },
  { id: 'business',       label: 'Onderneming',           description: 'Bedrijf, ZZP, KVK',            route: ROUTES.BUSINESS,        icon: <Briefcase     className="h-4 w-4" />, accent: 'amber' },
]

const ACCENT: Record<string, string> = {
  sage:    'bg-sage-50 text-sage-700',
  blue:    'bg-blue-50 text-blue-600',
  purple:  'bg-purple-50 text-purple-600',
  rose:    'bg-rose-50 text-rose-600',
  sky:     'bg-sky-50 text-sky-600',
  orange:  'bg-orange-50 text-orange-600',
  indigo:  'bg-indigo-50 text-indigo-600',
  amber:   'bg-amber-50 text-amber-700',
  emerald: 'bg-emerald-50 text-emerald-700',
  slate:   'bg-slate-100 text-slate-700',
}

// ── Hoofdcomponent ─────────────────────────────────────────────────────────────

export function DashboardPage() {
  const navigate = useNavigate()
  const { userId } = useAuthStore()
  const { currentStep, isCompleted: wizardDone, answers } = useWizardStore()

  const [modules,       setModules]       = useState<ModuleSummary[]>([])
  const [isLoading,     setIsLoading]     = useState(true)
  const [lastSaved,     setLastSaved]     = useState('')
  const [minorVPWarning, setMinorVPWarning] = useState(false)
  // V39: modules altijd zichtbaar - geen accordion meer

  useEffect(() => {
    if (!userId) return
    getAllModuleProgress(userId).then(async progressRecords => {
      const map = new Map<string, ModuleProgress>(progressRecords.map(p => [p.moduleName, p]))

      // Pragmatische aanvulling: controleer trusted_person direct via Dexie
      // zodat VP-status klopt ook als module_progress nog niet is bijgewerkt
      const vps = await loadTrustedPersons(userId)
      const hasActiveVP = vps.filter(p => p.confirmationStatus !== 'REVOKED').length > 0
      // V40C-P1: check of primaire VP minderjarig is (kind in dossier)
      const primaryVP = vps.find(p => p.role === 'PRIMARY' && p.confirmationStatus !== 'REVOKED')
      if (primaryVP?.relationship?.toLowerCase().includes('kind') || primaryVP?.relationship === 'child') {
        const childData = await loadChildren(userId)
        const matchChild = childData?.children?.find((c: { name?: string; dob?: string }) =>
          c.name?.toLowerCase() === primaryVP.firstName?.toLowerCase()
        )
        if (matchChild?.dob) {
          const ageMs = Date.now() - new Date(matchChild.dob).getTime()
          const age   = Math.floor(ageMs / (365.25 * 24 * 3600 * 1000))
          if (age >= 0 && age < 18) setMinorVPWarning(true)
        }
      }
      if (hasActiveVP && !map.has('trusted_person')) {
        // Simuleer een IN_PROGRESS record zodat dashboard VP als actief toont
        map.set('trusted_person', {
          id: '', userId, moduleName: 'trusted_person',
          completionPercentage: 100, stepsCurrent: 1, stepsTotal: 1,
          status: 'COMPLETE' as ModuleProgress['status'],
          startedAt: null, completedAt: null, lastVisitedAt: new Date().toISOString(),
          syncStatus: 'PENDING' as ModuleProgress['syncStatus'], cloudSyncId: null,
        })
      }

      // Aanvulling vanuit wizardStore.answers voor modules zonder module_progress.
      // Orde van prioriteit: module_progress (werkelijk opgeslagen) wint altijd.
      // Alleen als een module nog ontbreekt in de map, voegen we een heuristisch record toe.
      //
      // Heuristiek per module:
      // 'documents'      → gebruiker heeft woning of tweede woning → documentlocaties relevant
      // 'notarial'       → testament, levenstestament of onderneming → notarieel relevant
      // 'financial'      → beleggingen aanwezig → financieel module relevant
      // 'farewell'       → wizard doorlopen → persoonlijk afscheid als onderdeel getoond
      // 'digital_estate' → wizard doorlopen → digitale nalatenschap als onderdeel getoond
      const wizardWasDone = useWizardStore.getState().isCompleted
      const answerModuleMap: Array<[boolean, string, number]> = [
        // [conditie, moduleId, completionPercentage]
        [answers.hasOwnHome === true || answers.hasSecondHome === true,                          'documents',      30],
        [answers.hasTestament === true || answers.hasLivingWill === true || answers.hasBusiness === true, 'notarial', 30],
        [answers.hasInvestments === true,                                                        'financial',      30],
        [wizardWasDone,                                                                          'farewell',       20],
        [wizardWasDone,                                                                          'digital_estate', 20],
      ]
      for (const [condition, moduleId, pct] of answerModuleMap) {
        if (condition && !map.has(moduleId)) {
          map.set(moduleId, {
            id: '', userId, moduleName: moduleId,
            completionPercentage: pct, stepsCurrent: 1, stepsTotal: 3,
            status: 'IN_PROGRESS' as ModuleProgress['status'],
            startedAt: null, completedAt: null, lastVisitedAt: new Date().toISOString(),
            syncStatus: 'PENDING' as ModuleProgress['syncStatus'], cloudSyncId: null,
          })
        }
      }

      const summaries: ModuleSummary[] = MODULE_DEFS.map(def => ({
        ...def,
        progress:    map.get(def.id)?.completionPercentage ?? 0,
        status:      (map.get(def.id)?.status as ModuleSummary['status']) ?? 'NOT_STARTED',
        lastVisited: map.get(def.id)?.lastVisitedAt,
      }))
      summaries.sort((a, b) => {
        const order = { 'NOT_STARTED': 0, 'IN_PROGRESS': 1, 'COMPLETE': 2 }
        return order[a.status] - order[b.status]
      })
      setModules(summaries)
      const latest = progressRecords.reduce((a: ModuleProgress | null, b) =>
        !a || new Date(b.lastVisitedAt) > new Date(a.lastVisitedAt) ? b : a, null)
      if (latest) setLastSaved(latest.lastVisitedAt)
      setIsLoading(false)
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, JSON.stringify(answers), wizardDone])

  const doneCount  = modules.filter(m => m.status === 'COMPLETE').length
  const totalMods  = modules.length

  const reallyDone = wizardDone && doneCount >= Math.ceil(totalMods * 0.8)

  const hasTrustedPerson = modules.find(m => m.id === 'trusted_person')?.status === 'COMPLETE'
  const nextModule       = modules.find(m => m.status !== 'COMPLETE')

  // Wizardstap-naam voor primaire actieknop
  const wizardStepLabel = STEP_LABELS[currentStep] ?? `Stap ${currentStep + 1}`

  // Aandachtspunten
  const attentionPoints: Array<{ text: string; route: string }> = []
  if (!hasTrustedPerson)
    attentionPoints.push({ text: 'Vertrouwenspersoon nog niet ingesteld', route: ROUTES.TRUSTED_PERSON })
  if (answers.hasChildren && modules.find(m => m.id === 'notarial')?.status === 'NOT_STARTED')
    attentionPoints.push({ text: 'Voogdij voor kinderen nog niet vastgelegd', route: ROUTES.NOTARIAL })
  if (answers.hasOwnHome && modules.find(m => m.id === 'documents')?.status === 'NOT_STARTED')
    attentionPoints.push({ text: 'Hypotheekgegevens nog niet gedocumenteerd', route: ROUTES.DOCUMENTS })
  if (answers.hasInvestments && modules.find(m => m.id === 'financial')?.status === 'NOT_STARTED')
    attentionPoints.push({ text: 'Beleggingen nog niet vastgelegd', route: ROUTES.FINANCIAL })

  return (
    <div className="flex flex-col gap-5 animate-fade-in">

      {/* ── ZONE A - Boven de vouw ─────────────────────────────────────────── */}

      {/* Hero - voorbereiding */}
      <div className="rounded-2xl bg-gradient-to-br from-moss to-sage-700 p-6 text-white shadow-elevated">
        <p className="text-sm text-white/70 mb-1">WilsApp</p>
        <p className="text-xl font-semibold text-white">Uw voorbereiding</p>
        <p className="text-sm text-white/70 mt-1 leading-relaxed">Leg vast wat voor u belangrijk is, nu u dat zelf kunt bepalen.</p>
        {lastSaved && (
          <p className="text-xs text-white/60 mt-2">Bijgewerkt {formatDateNL(lastSaved)}</p>
        )}
      </div>

      {/* Mijn gegevens - altijd zichtbaar bovenaan */}
      <PersonalDataCard />

      {/* Aandachtspunten - direct zichtbaar als aanwezig */}
      {attentionPoints.length > 0 && (
        <div className="flex flex-col gap-2">
          {attentionPoints.map((pt, i) => (
            <button key={i} onClick={() => navigate(pt.route)}
              className="flex items-start gap-3 rounded-xl border border-status-warnBorder bg-status-warnBg px-4 py-3.5 text-left shadow-soft hover:shadow-card transition-all duration-200">
              <AlertCircle className="h-4 w-4 text-status-warnText flex-shrink-0 mt-0.5" />
              <span className="text-sm text-status-warnText flex-1">{pt.text}</span>
              <ArrowRight className="h-3.5 w-3.5 text-status-warnText/60 flex-shrink-0 mt-0.5" />
            </button>
          ))}
        </div>
      )}

      {/* Wizardkaart - altijd actie tonen zolang < 80% modules klaar */}
      {!reallyDone ? (
        <button
          onClick={() => navigate(wizardDone && nextModule ? nextModule.route : WIZARD_ROUTE)}
          className="flex items-center gap-4 rounded-xl bg-moss text-white px-5 py-4 text-left shadow-soft transition-all duration-150 ease-soft hover:bg-moss-light active:bg-sage-900 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sage-600"
        >
          <div className="flex-1">
            <p className="text-xs font-semibold text-white/80 uppercase tracking-wider mb-1">
              {wizardDone ? 'Onderdelen aanvullen' : 'Volgende stap'}
            </p>
            <p className="text-base font-semibold text-white">
              {wizardDone && nextModule
                ? `Volgende openstaand: ${nextModule.label}`
                : `Volgende stap: ${wizardStepLabel}`}
            </p>

            {wizardDone && (
              <p className="text-xs text-white/70 mt-1">
                Vul de ontbrekende onderdelen aan om uw voorbereiding compleet te maken.
              </p>
            )}
          </div>
          <ArrowRight className="h-5 w-5 text-white flex-shrink-0" />
        </button>
      ) : (
        <div className="flex items-center gap-3 rounded-2xl border border-sage-200 bg-status-successBg px-5 py-4">
          <CheckCircle2 className="h-5 w-5 text-sage-600 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-bold text-sage-800">Voorbereiding voltooid</p>
            <p className="text-xs text-sage-600 mt-0.5">Minimaal 80% van de onderdelen is ingevuld. U kunt altijd aanvullen.</p>
          </div>
        </div>
      )}

      {/* V39: Aanbevolen volgende stap verwijderd - was duplicaat van wizardkaart */}

      {/* V40C-P1: Minderjarige VP waarschuwing */}
      {minorVPWarning && (
        <div className="flex items-start gap-3 rounded-2xl bg-amber-50 border border-amber-200 px-4 py-3 cursor-pointer"
          onClick={() => navigate(ROUTES.TRUSTED_PERSON)}>
          <span className="text-base flex-shrink-0">⚠️</span>
          <div>
            <p className="text-sm font-semibold text-amber-900">Eerste vertrouwenspersoon is minderjarig</p>
            <p className="text-xs text-amber-700 mt-0.5">
              Overweeg een volwassen eerste of tweede vertrouwenspersoon aan te wijzen.
            </p>
            <p className="text-xs text-amber-600 mt-1 underline underline-offset-2">Klik hier om dit te bekijken →</p>
          </div>
        </div>
      )}

      {/* Levenslijn - geïntegreerde kennisomgeving, één tik naar de juiste stap */}
      <button
        onClick={() => navigate(ROUTES.LEVENSLIJN)}
        className="flex items-center gap-4 rounded-2xl bg-white border border-sand-300 px-5 py-4 text-left shadow-soft hover:shadow-card hover:border-sage-300 hover:bg-sage-50 transition-all duration-200"
      >
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-sage-50 text-sage-600">
          <Compass className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-base font-semibold text-ink">Waar staat u nu?</p>
          <p className="text-xs text-ink-4 mt-0.5">Kies uw situatie en ga in één tik naar de juiste stap.</p>
        </div>
        <ArrowRight className="h-5 w-5 text-sage-600 flex-shrink-0" />
      </button>

      {/* ── ZONE B - Alle onderdelen (altijd zichtbaar, V39) ──────────────── */}
      <div>
        <p className="text-xs font-semibold text-ink-4 uppercase tracking-wider px-1 mb-2">
          Alle onderdelen
        </p>
        {isLoading ? <SkeletonList count={4} /> : (
          <div className="grid gap-2.5 sm:grid-cols-2">
            {modules.map(mod => {
              const def = MODULE_DEFS.find(d => d.id === mod.id)
              return (
                <button key={mod.id} onClick={() => navigate(mod.route)}
                  className="flex items-center gap-3 rounded-2xl bg-white border border-sand-300 shadow-soft px-4 py-3.5 text-left hover:border-sage-300 hover:shadow-card hover:bg-sage-50/40 transition-all duration-150">
                  <div className={cn('flex h-9 w-9 items-center justify-center rounded-xl flex-shrink-0', ACCENT[def?.accent ?? 'sage'])}>
                    {mod.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-ink">{mod.label}</p>
                    <p className="text-xs text-ink-4 mt-0.5 truncate">{mod.description}</p>
                  </div>
                  <StatusPill
                    status={moduleStatusKind(mod.status)}
                    label={mod.status === 'COMPLETE' ? 'Gereed' : mod.status === 'IN_PROGRESS' ? 'Bezig' : 'Niet begonnen'}
                    size="sm"
                    className="flex-shrink-0"
                  />
                </button>
              )
            })}
          </div>
        )}
      </div>

      {/* V39-P1: voortgangskaart verwijderd - geen percentage/teller tonen */}
      <div className="flex items-center gap-2 rounded-xl bg-sage-50 border border-sage-200 px-4 py-3">
        <ShieldCheck className="h-4 w-4 text-sage-600 flex-shrink-0" />
        <p className="text-xs text-sage-700">Versleuteld opgeslagen op uw apparaat</p>
      </div>

      {/* Terug naar introductie - secundair (V39) */}
      <button
        onClick={() => navigate(WIZARD_ROUTE + '?welcome=1')}
        className="flex items-center justify-center gap-1.5 py-2 text-xs text-ink-4 hover:text-ink-3 transition-colors"
      >
        Bekijk de introductie opnieuw
        <ArrowRight className="h-3 w-3" />
      </button>


    </div>
  )
}
